import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskedLine, Reveal, FadeIn } from "@/components/Reveal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SERVICES, FAQ, METHOD_SERVICES } from "@/data/content";

const Services = () => (
  <div data-testid="services-page">
    {/* ————— HERO ————— */}
    <section className="px-6 pb-20 pt-44 sm:px-10 sm:pb-28 sm:pt-56">
      <div className="mx-auto max-w-6xl">
        <FadeIn delay={0.2}>
          <p className="overline-label">Nos Services</p>
        </FadeIn>
        <h1 className="mt-10 max-w-4xl font-serif font-light leading-[1.05] tracking-tight text-ivoire">
          <MaskedLine delay={0.4} className="text-4xl sm:text-6xl lg:text-7xl">
            Une communication pensée pour
          </MaskedLine>
          <MaskedLine delay={0.58} className="text-4xl sm:text-6xl lg:text-7xl">
            <em className="italic text-champagne">faire grandir votre entreprise.</em>
          </MaskedLine>
        </h1>
        <FadeIn delay={0.9}>
          <p className="mt-10 max-w-2xl text-base leading-relaxed text-ivoire/65 sm:text-lg">
            Votre image est un investissement. Elle influence la confiance, la
            mémorisation et la perception de votre activité. Rémanence Studio
            conçoit des supports de communication cohérents, durables et adaptés à
            vos objectifs.
          </p>
        </FadeIn>
      </div>
    </section>

    {/* ————— BENTO ————— */}
    <section className="mx-auto max-w-7xl px-6 pb-28 sm:px-10 sm:pb-40">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
        {SERVICES.map((s, i) => (
          <Reveal key={s.num} delay={(i % 2) * 0.08} className={s.span}>
            <article
              data-testid={`service-card-${s.num}`}
              className="group flex h-full flex-col border border-white/8 bg-charbon/60 p-8 transition-[border-color,transform] duration-500 hover:-translate-y-1 hover:border-champagne/40 sm:p-12"
            >
              <div className="flex items-start justify-between gap-6">
                <span className="font-serif text-sm italic text-champagne/70">{s.num}</span>
                <span className="rounded-full border border-champagne/40 px-4 py-1.5 text-[11px] uppercase tracking-[0.18em] text-champagne">
                  {s.price}
                </span>
              </div>
              <h2 className="mt-8 font-serif text-2xl font-light tracking-tight text-ivoire transition-colors duration-400 group-hover:text-champagne sm:text-4xl">
                {s.title}
              </h2>
              <p className="mt-3 font-serif text-lg font-light italic text-ivoire/70 sm:text-xl">
                {s.tagline}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-ivoire/55">{s.description}</p>
              <ul className="mt-8 flex flex-wrap gap-2.5">
                {s.items.map((item) => (
                  <li
                    key={item}
                    className="flex items-center gap-2 border border-white/8 px-3.5 py-1.5 text-xs text-ivoire/65"
                  >
                    <span className="h-1 w-1 rotate-45 bg-champagne/80" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to={`/contact?service=${encodeURIComponent(s.title)}`}
                data-testid={`service-devis-${s.num}`}
                className="group/link mt-auto inline-flex w-fit items-center gap-3 pt-10 text-[12px] uppercase tracking-[0.2em] text-champagne"
              >
                Demander un devis
                <ArrowUpRight size={15} strokeWidth={1.5} className="transition-transform duration-400 group-hover/link:-translate-y-0.5 group-hover/link:translate-x-0.5" />
              </Link>
            </article>
          </Reveal>
        ))}
      </div>
    </section>

    {/* ————— MÉTHODE ————— */}
    <section className="border-y border-white/5 bg-charbon/40 py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-10">
        <Reveal className="mb-20 text-center">
          <p className="overline-label">Notre méthode</p>
          <h2 className="mt-6 font-serif text-3xl font-light tracking-tight text-ivoire sm:text-5xl">
            Un déroulé <em className="italic text-champagne">clair et maîtrisé</em>
          </h2>
        </Reveal>
        <div className="grid gap-px overflow-hidden border border-white/8 bg-white/8 sm:grid-cols-2 lg:grid-cols-4">
          {METHOD_SERVICES.map((step, i) => (
            <Reveal key={step.num} delay={i * 0.08} className="h-full">
              <div data-testid={`services-method-${step.num}`} className="flex h-full flex-col bg-noir p-10">
                <span className="font-serif text-4xl font-light italic text-champagne/80">{step.num}</span>
                <h3 className="mt-8 font-serif text-2xl font-light text-ivoire">{step.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-ivoire/55">{step.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* ————— FAQ ————— */}
    <section className="mx-auto max-w-4xl px-6 py-24 sm:px-10 sm:py-32">
      <Reveal className="mb-14 text-center">
        <p className="overline-label">Questions fréquentes</p>
        <h2 className="mt-6 font-serif text-3xl font-light tracking-tight text-ivoire sm:text-5xl">
          Ce que l'on nous demande souvent
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
              <AccordionTrigger
                data-testid={`faq-question-${i}`}
                className="py-7 text-left font-serif text-xl font-light text-ivoire transition-colors hover:text-champagne hover:no-underline sm:text-2xl"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="pb-7 text-base leading-relaxed text-ivoire/60">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </section>

    {/* ————— CTA ————— */}
    <section className="border-t border-white/5 py-28 sm:py-36">
      <div className="mx-auto max-w-3xl px-6 text-center sm:px-10">
        <Reveal>
          <h2 className="font-serif text-3xl font-light leading-tight tracking-tight text-ivoire sm:text-5xl">
            Prêt à donner une nouvelle dimension à{" "}
            <em className="italic text-champagne">votre communication ?</em>
          </h2>
          <p className="mt-8 text-base text-ivoire/60">Parlons de votre projet.</p>
          <Link
            to="/contact"
            data-testid="services-final-cta"
            className="group mt-10 inline-flex items-center gap-3 rounded-full bg-champagne px-10 py-4 text-[13px] uppercase tracking-[0.2em] text-noir transition-[background-color] duration-400 hover:bg-ivoire"
          >
            Demander un devis
            <ArrowUpRight size={15} strokeWidth={1.5} className="transition-transform duration-400 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
        </Reveal>
      </div>
    </section>
  </div>
);

export default Services;
